import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, Switch, FormControlLabel, Button,
  CircularProgress, Alert, Snackbar, Select, FormControl, InputLabel,
} from '@mui/material';
import { CheckRounded } from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { useBusiness, UpdateBusinessRequest } from '../../hooks/useBusiness';
import { useCategory } from '../../hooks/useCategory';
import { COUNTRIES } from '../../constants/countries';
import { buildFullAddress, geocodeAddress } from '../../utils/geocoding';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px', p: 2.5 };
const grid2 = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };
const grid4 = { display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4,1fr)' }, gap: 2 };

interface FormState {
  business_name: string; business_category: string; other_category_name: string; business_email: string;
  phone_dial_code: string; phone_number: string;
  street_address: string; city: string; state_name: string; postal_code: string; country_name: string;
  gst_number: string; same_as_business: boolean;
  registered_business_name: string; billing_email: string; billing_phone_dial_code: string; billing_phone_number: string;
  billing_street_address: string; billing_city: string; billing_state_name: string; billing_postal_code: string; billing_country_name: string;
}

const empty: FormState = {
  business_name: '', business_category: '', other_category_name: '', business_email: '', phone_dial_code: '+91', phone_number: '',
  street_address: '', city: '', state_name: '', postal_code: '', country_name: '', gst_number: '', same_as_business: true,
  registered_business_name: '', billing_email: '', billing_phone_dial_code: '+91', billing_phone_number: '',
  billing_street_address: '', billing_city: '', billing_state_name: '', billing_postal_code: '', billing_country_name: '',
};

/** Labeled country-code select + phone number — consistent height & alignment. */
const PhoneField: React.FC<{ code: string; phone: string; label: string; onCode: (v: string) => void; onPhone: (v: string) => void }> = ({ code, phone, label, onCode, onPhone }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <FormControl sx={{ width: 120, flexShrink: 0 }}>
      <InputLabel>Code</InputLabel>
      <Select label="Code" value={code} onChange={(e) => onCode(e.target.value)}
        renderValue={(v) => { const c = COUNTRIES.find((x) => x.dialCode === v); return <span>{c?.flag} {v as string}</span>; }}>
        {COUNTRIES.map((c) => <MenuItem key={c.id} value={c.dialCode}>{c.flag} {c.name} ({c.dialCode})</MenuItem>)}
      </Select>
    </FormControl>
    <TextField fullWidth label={label} value={phone} onChange={(e) => onPhone(e.target.value)} />
  </Box>
);

const BusinessDetailsPage: React.FC = () => {
  const { getMyBusiness, updateBusiness } = useBusiness();
  const { categories, fetchCategories } = useCategory();
  const [form, setForm] = useState<FormState>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [isOtherMode, setIsOtherMode] = useState(false);

  // "Other" category handling — mirrors AddBusiness: hide Other from the dropdown,
  // offer "Add Other" → free-text custom name sent as other_category_name.
  const otherCategory = useMemo(() => categories.find((c) => c.name.toLowerCase() === 'other'), [categories]);
  const categoryOptions = useMemo(() => categories.filter((c) => c.name.toLowerCase() !== 'other'), [categories]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  useEffect(() => {
    (async () => {
      try {
        const b = await getMyBusiness();
        if (b) {
          setIsOtherMode(!!b.other_category_name);
          setForm({
            business_name: b.name || '', business_category: b.category_id || '', other_category_name: b.other_category_name || '', business_email: b.email || '',
            phone_dial_code: b.phone_country_code || '+91', phone_number: b.phone_number || '',
            street_address: b.street_address || '', city: b.city || '', state_name: b.state_name || '',
            postal_code: b.postal_code || '', country_name: b.country_name || '', gst_number: b.gstin || '',
            same_as_business: b.same_as_business ?? true,
            registered_business_name: b.billing_name || '', billing_email: b.billing_email || '', billing_phone_dial_code: b.billing_country_code || '+91', billing_phone_number: b.billing_phone || '',
            billing_street_address: b.billing_street_address || '', billing_city: b.billing_city || '', billing_state_name: b.billing_state_name || '',
            billing_postal_code: b.billing_postal_code || '', billing_country_name: b.billing_country_name || '',
          });
        }
      } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load business.'); }
      finally { setLoading(false); }
    })();
  }, [getMyBusiness]);

  const set = (k: keyof FormState, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.business_name.trim()) return setError('Enter a business name.');
    if (isOtherMode && !form.other_category_name.trim()) return setError('Enter the category name for "Other".');
    setSaving(true); setError(null);

    let lat: number | null | undefined;
    let lng: number | null | undefined;
    try {
      const full = buildFullAddress({ streetAddress: form.street_address, city: form.city, state: form.state_name, country: form.country_name, postalCode: form.postal_code });
      const geo = await geocodeAddress(full);
      if (geo) { lat = geo.latitude; lng = geo.longitude; }
    } catch { /* keep existing coords */ }

    const payload: UpdateBusinessRequest = {
      business_name: form.business_name, business_category: form.business_category || undefined,
      ...(isOtherMode && form.other_category_name.trim() ? { other_category_name: form.other_category_name.trim() } : {}),
      business_email: form.business_email, phone_number: form.phone_number, phone_dial_code: form.phone_dial_code,
      street_address: form.street_address, city: form.city, state_name: form.state_name,
      postal_code: form.postal_code, country_name: form.country_name, gst_number: form.gst_number,
      ...(lat != null && lng != null ? { latitude: lat, longitude: lng } : {}),
      same_as_business: form.same_as_business,
      ...(form.same_as_business ? {} : {
        registered_business_name: form.registered_business_name, billing_email: form.billing_email,
        billing_phone_number: form.billing_phone_number, billing_phone_dial_code: form.billing_phone_dial_code,
        billing_phone_country_code: form.billing_phone_dial_code, billing_street_address: form.billing_street_address,
        billing_city: form.billing_city, billing_state_name: form.billing_state_name,
        billing_postal_code: form.billing_postal_code, billing_country_name: form.billing_country_name,
      }),
    };

    try { await updateBusiness(payload); setToast('Business details saved'); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 800 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={card}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">Business profile</Typography>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>Visible to customers</Typography>
        </Box>
        <Box sx={{ ...grid2, mb: 2 }}>
          <TextField fullWidth label="Business name" value={form.business_name} onChange={(e) => set('business_name', e.target.value)} />
          {isOtherMode ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth label="Other category name" value={form.other_category_name} onChange={(e) => set('other_category_name', e.target.value)} placeholder="Enter your category name" />
              <Button variant="outlined" onClick={() => { setIsOtherMode(false); set('other_category_name', ''); set('business_category', ''); }} sx={{ flexShrink: 0 }}>Change</Button>
            </Box>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={form.business_category}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__add_other__') {
                    if (otherCategory) { setIsOtherMode(true); set('business_category', otherCategory.id); set('other_category_name', ''); }
                  } else { set('business_category', v); }
                }}
              >
                {categoryOptions.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                {otherCategory && <MenuItem value="__add_other__">+ Add Other</MenuItem>}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box sx={{ ...grid2, mb: 2 }}>
          <TextField fullWidth label="Email" value={form.business_email} onChange={(e) => set('business_email', e.target.value)} />
          <PhoneField label="Phone" code={form.phone_dial_code} phone={form.phone_number}
            onCode={(v) => set('phone_dial_code', v)} onPhone={(v) => set('phone_number', v)} />
        </Box>
        <TextField fullWidth label="Street address" value={form.street_address} onChange={(e) => set('street_address', e.target.value)} sx={{ mb: 2 }} />
        <Box sx={{ ...grid4, mb: 2 }}>
          <TextField fullWidth label="City" value={form.city} onChange={(e) => set('city', e.target.value)} />
          <TextField fullWidth label="State" value={form.state_name} onChange={(e) => set('state_name', e.target.value)} />
          <TextField fullWidth label="Postal code" value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} />
          <TextField fullWidth label="Country" value={form.country_name} onChange={(e) => set('country_name', e.target.value)} />
        </Box>
        <Box sx={{ maxWidth: { sm: '50%' } }}>
          <TextField fullWidth label="GST number (optional)" value={form.gst_number} onChange={(e) => set('gst_number', e.target.value)} />
        </Box>
      </Box>

      <Box sx={{ ...card, mt: 2.25 }}>
        <FormControlLabel control={<Switch checked={form.same_as_business} onChange={(e) => set('same_as_business', e.target.checked)} />} label="Billing address same as business address" />
        {!form.same_as_business && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ ...grid2, mb: 2 }}>
              <TextField fullWidth label="Registered business name" value={form.registered_business_name} onChange={(e) => set('registered_business_name', e.target.value)} />
              <TextField fullWidth label="Billing email" value={form.billing_email} onChange={(e) => set('billing_email', e.target.value)} />
            </Box>
            <Box sx={{ ...grid2, mb: 2 }}>
              <PhoneField label="Billing phone" code={form.billing_phone_dial_code} phone={form.billing_phone_number}
                onCode={(v) => set('billing_phone_dial_code', v)} onPhone={(v) => set('billing_phone_number', v)} />
              <TextField fullWidth label="Billing street address" value={form.billing_street_address} onChange={(e) => set('billing_street_address', e.target.value)} />
            </Box>
            <Box sx={grid4}>
              <TextField fullWidth label="City" value={form.billing_city} onChange={(e) => set('billing_city', e.target.value)} />
              <TextField fullWidth label="State" value={form.billing_state_name} onChange={(e) => set('billing_state_name', e.target.value)} />
              <TextField fullWidth label="Postal code" value={form.billing_postal_code} onChange={(e) => set('billing_postal_code', e.target.value)} />
              <TextField fullWidth label="Country" value={form.billing_country_name} onChange={(e) => set('billing_country_name', e.target.value)} />
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" startIcon={<CheckRounded />} disabled={saving} onClick={save}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save changes'}
        </Button>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessDetailsPage;
